import React, { FC, ReactElement, useCallback, useContext, useMemo, useState } from 'react'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import {
    DocumentViewer as DocumentViewerLanguage,
    TermsAndConditions as TermsAndConditionsLanguage
} from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { DocumentViewer } from '../DocumentViewer/DocumentViewer'
import { WelcomeContainer } from '../WelcomeContainer/WelcomeContainer'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../React/Common/Hooks/useBranding'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { Tags } from '../../../../React/Common/API/Contentful/Constants'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { performAuthOutflowRouting } from '../../../Utilities/RoutingUtils'
import { acceptTermsOfService, rejectTermsOfService } from '../../../../NonReact/common/API/CurrentUserAPI'

export const TermsAndConditions: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(TermsAndConditionsLanguage.NAME)

    const [userInfo]: UserInfo = useUserInfo(true)

    const branding: Nullable<Branding> = useBranding()
    const navigate: NavigateFunction = useNavigate()
    const location: Location = useLocation()

    const [accept, setAccept]: ReactState<boolean> = useState<boolean>(false)

    const requiresAcceptance: boolean = useMemo((): boolean => userInfo?.Advanced?.PromptForTerms ?? false, [userInfo])

    const moveToNextLocation = useCallback(
        (): void => performAuthOutflowRouting(navigate, location, branding),
        [userInfo, branding]
    )

    return (
        <WelcomeContainer>
            <DocumentViewer
                tag={Tags.TERMS_AND_CONDITIONS}
                bottomLeft={
                    requiresAcceptance ? (
                        <RouterCheckbox
                            id='accept-checkbox'
                            onChange={setAccept}
                            isChecked={accept}
                            labelRight={language.get(TermsAndConditionsLanguage.TermsAndConditions_AcceptText)}
                        />
                    ) : (
                        <></>
                    )
                }
                bottomRight={
                    <>
                        {requiresAcceptance ? (
                            <RoundedButton
                                className='dvaa-accept-button'
                                disabled={requiresAcceptance && !accept}
                                onClick={(): void => {
                                    if (userInfo) {
                                        acceptTermsOfService().then(moveToNextLocation).catch(displayError)
                                    }
                                }}
                            >
                                {language.get(DocumentViewerLanguage.DocumentViewer_AcceptButton)}
                            </RoundedButton>
                        ) : (
                            <></>
                        )}
                        <RoundedButton
                            className='dvaa-close-button'
                            disabled={false}
                            onClick={(): void => {
                                rejectTermsOfService().then(moveToNextLocation).catch(displayError)
                            }}
                        >
                            {language.get(DocumentViewerLanguage.DocumentViewer_CloseButton)}
                        </RoundedButton>
                    </>
                }
                showDefaultCloseButton={false}
            />
        </WelcomeContainer>
    )
}
